import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import TagClientService from 'views/CRM/Tags/services/TagClientService';

/**
 * @typedef {Object} Tag
 * @property {number} id - ID único da tag
 * @property {string} name - Nome da tag
 * @property {boolean} disabled - Estado de desativação da tag
 * @property {string} color - Cor da tag
 */

/**
 * @typedef {Object} TagsState
 * @property {Tag[]} tags - Lista de tags carregadas
 * @property {number} totalItems - Número total de tags disponíveis
 * @property {number} activePage - Página atual da paginação
 * @property {boolean} isLoading - Indica se está carregando dados
 */

/**
 * @typedef {Object} UseGetTagsReturn
 * @property {TagsState} state - Estado atual do hook
 * @property {(page?: number) => Promise<void>} getTags - Função para buscar tags
 */

const initialState = {
  tags: [],
  totalItems: 0,
  activePage: 1,
  isLoading: false
};

/**
 * Hook para gerenciar listagem de tags com paginação
 * @returns {TagsState} Estado atual do hook
 * @example
 * function TagsList() {
 *   const { tags, isLoading, activePage, totalItems } = useGetTags();
 *
 *   if (isLoading) return <Loading />;
 *
 *   return (
 *     <div>
 *       {tags.map(tag => (
 *         <div key={tag.id} style={{ color: tag.color }}>{tag.name}</div>
 *       ))}
 *       <Pagination
 *         activePage={activePage}
 *         itemsCountPerPage={10}
 *         totalItemsCount={totalItems}
 *         onChange={setCurrentPage}
 *       />
 *     </div>
 *   );
 * }
 */
const useGetTags = () => {
  const [state, setState] = useState(initialState);

  useEffect(() => {
    setState((prev) => ({ ...prev, isLoading: true }));

    TagClientService.getAll(state.activePage)
      .then((response) => {
        setState({
          tags: response.data.data,
          totalItems: response.data.totalRegisters,
          activePage: state.activePage,
          isLoading: false
        });
      })
      .catch(() => {
        toast.error('Não foi possível listar as tags', { autoClose: 3000 });
        setState((prev) => ({ ...prev, isLoading: false }));
      });
  }, [state.activePage]);

  return state;
};

export default useGetTags;
